import { BaseRepository } from '../../../infra/base.repository'
import { ConfigService } from '../../../infra/config'
import type { FormV2Form } from './form-v2-form'

export class FormV2FormsRepository extends BaseRepository<FormV2Form> {
    constructor(
        private config: ConfigService = new ConfigService(),
        token?: string,
        cache?: any,
    ) {
        super(config.toolsApiUrl + '/forms-v2/forms', token, cache)
    }

    // Query key é "t", não "token", em todas as rotas públicas abaixo: "token" já é o parâmetro usado
    // em toda a API para o JWT da sessão logada, e colidia com este quando o link era aberto autenticado.
    async publicGetOne(formV2FormLinkId: string, token: string) {
        const { json, statusCode, message } = await this.request.get(this.url + '/public/' + formV2FormLinkId + '?t=' + token)
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async publicUpdate(formV2FormLinkId: string, token: string, dto: Partial<FormV2Form>) {
        const { json, statusCode, message } = await this.request.patch(this.url + '/public/' + formV2FormLinkId + '?t=' + token, dto)
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async publicUploadFile(formV2FormLinkId: string, token: string, content: string) {
        const { json, statusCode, message } = await this.request.post(this.url + '/public/' + formV2FormLinkId + '/upload?t=' + token, { content })
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async publicGetSignatureReport(formV2FormLinkId: string, token: string) {
        const { json, statusCode, message } = await this.request.get(
            this.url + '/public/' + formV2FormLinkId + '/signature-report?t=' + token,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async publicStartSigning(formV2FormLinkId: string, token: string) {
        const { json, statusCode, message } = await this.request.post(
            this.url + '/public/' + formV2FormLinkId + '/start-signing?t=' + token,
            {},
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        // Erro estruturado (cadastro de assinante incompleto) — ver comentário em startSigning acima.
        if (json?.code === 'MISSING_SIGNER_INFO') throw json
        throw message
    }

    async publicGetContinueSigningLink(formV2FormLinkId: string, token: string) {
        const { json, statusCode, message } = await this.request.get(
            this.url + '/public/' + formV2FormLinkId + '/continue-signing-link?t=' + token,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async startSigning(formV2FormId: string, mode: number) {
        const { json, statusCode, message } = await this.request.post(
            this.url + '/' + formV2FormId + '/start-signing?token=' + this.resolvedToken,
            { mode },
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        // Erro estruturado (cadastro de assinante incompleto) — o chamador precisa do corpo inteiro
        // (json.signers), não só da mensagem, pra listar quem está com o cadastro incompleto.
        if (json?.code === 'MISSING_SIGNER_INFO') throw json
        throw message
    }

    async stopSigning(formV2FormId: string) {
        const { json, statusCode, message } = await this.request.post(
            this.url + '/' + formV2FormId + '/stop-signing?token=' + this.resolvedToken,
            {},
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async dynamicTableAddRow(formV2FormId: string, blockId: string, timezoneOffsetMinutes: number) {
        const { json, statusCode, message } = await this.request.post(
            this.url + '/' + formV2FormId + '/dynamic-table/add-row?token=' + this.resolvedToken,
            { blockId, timezoneOffsetMinutes },
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async dynamicTableSignRowInPerson(formV2FormId: string, dto: { blockId: string; rowId: string; columnId: string; imageDataUrl: string }) {
        const { json, statusCode, message } = await this.request.post(
            this.url + '/' + formV2FormId + '/dynamic-table/sign-row?token=' + this.resolvedToken,
            dto,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async dynamicTableCreateRowSignLink(formV2FormId: string, dto: { blockId: string; rowId: string; columnId: string }) {
        const { json, statusCode, message } = await this.request.post(
            this.url + '/' + formV2FormId + '/dynamic-table/row-signature-link?token=' + this.resolvedToken,
            dto,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    // Mesmo formato de form-datas.repository.ts's findLastEmissionsFromPerson (v1) — usado pelo gate
    // de "documentos obrigatórios" do agendamento pra checar templates v2.
    async findLastEmissionsFromPerson(personId: string, formV2TemplateIds: string[]) {
        const { json, statusCode, message } = await this.request.get(
            this.url + '/last-emissions?personId=' + personId + '&ids=' + formV2TemplateIds + '&token=' + this.resolvedToken,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async publicGetRowSign(token: string) {
        const { json, statusCode, message } = await this.request.get(this.url + '/public/row-sign/' + token)
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async publicSubmitRowSign(token: string, imageDataUrl: string) {
        const { json, statusCode, message } = await this.request.post(this.url + '/public/row-sign/' + token, { imageDataUrl })
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }
}
